import { useRouter } from 'next/router'
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useMediaQuery } from 'usehooks-ts'

import { type SanityCombinedListingProductFragment } from '@data/sanity/queries/types/product'
import { getSanityImageUrl } from '@lib/image'
import { LanguageContext } from '@lib/language-context'
import { colorOptionNames } from '@lib/product/product'
import {
  type CombinedListingParameterMap,
  type CombinedListingStep,
  getIsEmptyFrame,
  ProductCombinedListingContext,
  useCombinedListingProductPrice,
  useFrameSettings,
} from '@lib/product/combined-listing'
import { ProductContext } from '@lib/product/context'
import { getPageUrl } from '@lib/routes'
import { useCombinedListingCartAttributes } from '@lib/shopify/attribute'

import CombinedListingFramePreviewSettings from '@blocks/product/combined-listing-frame-preview-settings'
import CombinedListingProductActions from '@blocks/product/combined-listing-product-actions'
import CombinedListingProductSelect from '@blocks/product/combined-listing-product-select'
import ProductGallery from '@blocks/product/product-gallery'
import ProductPrice from '@blocks/product/product-price'
import SimplePortableText from '@components/simple-portable-text'

const CombinedListingProductHero = () => {
  const { locale } = useContext(LanguageContext)
  const {
    combinedListingActivePassepartoutHoleSize,
    combinedListingActiveProducts,
    combinedListingActiveVariants,
    combinedListingActiveSize,
    setCombinedListingStateManagerType,
    setCombinedListingSettings,
    setCombinedListingConfiguration,
    setCombinedListingMainProductId,
    setCombinedListingSize,
    setCombinedListingPassepartoutHoleSize,
    clearCombinedListingProductType,
  } = useContext(ProductCombinedListingContext)
  const { product } = useContext(ProductContext)

  const router = useRouter()

  const [openStep, setOpenStep] = useState<CombinedListingStep | undefined>()
  const [artworkUrl, setArtworkUrl] = useState<string | null>(null)
  const [backgroundColor, setBackgroundColor] = useState('#f7f7f7')

  const productGalleryWrapperRef = useRef<HTMLDivElement | null>(null)

  const isScreenSmallOrLarger = useMediaQuery(`(min-width: 768px)`)

  const frameProduct = useMemo(
    () =>
      product?.productCombinedListing?.frame?.find(
        (frameProduct) => frameProduct._id === product._id,
      ),
    [product],
  )

  const frameColor = useMemo(() => {
    // Get frame color from product options
    const colorOption = product?.options?.find((option) =>
      colorOptionNames.includes(option.name),
    )

    if (colorOption) {
      return colorOption.values[0]
    }

    // Get frame color from frame builder texture title
    if (frameProduct) {
      return frameProduct.combinedListingFrameBuilderProfile?.texture?.title
    }
  }, [frameProduct, product])

  const [price, comparePrice] = useCombinedListingProductPrice(
    combinedListingActiveProducts,
    combinedListingActiveVariants,
  )

  // const sku = useMemo(() => {
  //   if (!product || !combinedListingActiveSize) {
  //     return
  //   }

  //   // Product SKU (product ID with size)
  //   if (
  //     !combinedListingSettings ||
  //     !combinedListingConfiguration ||
  //     combinedListingActiveProducts.length === 0
  //   ) {
  //     return product.sku
  //   }

  //   // Generated SKU (product ID with size and extra product IDs)
  //   const productIds = combinedListingActiveProducts?.map(
  //     (combinedListingActiveProduct) => combinedListingActiveProduct.productID,
  //   ) ?? []
  //   return getCombinedListingVariantSku(
  //     combinedListingSettings,
  //     combinedListingConfiguration,
  //     productIds,
  //     combinedListingActiveSize,
  //   )
  // }, [
  //   combinedListingActiveSize,
  //   combinedListingConfiguration,
  //   combinedListingActiveProducts,
  //   combinedListingSettings,
  //   product,
  // ])

  const isEmptyFrame = useMemo(
    () => getIsEmptyFrame(combinedListingActiveProducts ?? []),
    [combinedListingActiveProducts],
  )

  const defaultArtworkUrl = useMemo(() => {
    if (!product?.frameBuilderDefaultFrame?.previewImage) {
      return null
    }

    return (
      getSanityImageUrl(product.frameBuilderDefaultFrame.previewImage) ?? null
    )
  }, [product])

  const predefinedBackgroundColors = useMemo(() => {
    if (!product?.frameBuilderDefaultFrame?.backgroundColorPresets) {
      return []
    }

    return product.frameBuilderDefaultFrame.backgroundColorPresets.map(
      (backgroundColorPreset) => backgroundColorPreset.hex,
    )
  }, [product])

  const combinedListingCartAttributes = useCombinedListingCartAttributes()

  const handleMainProductChange = useCallback(
    (product: SanityCombinedListingProductFragment) => {
      // Update combined listing state
      clearCombinedListingProductType('glass')
      clearCombinedListingProductType('backboard')
      clearCombinedListingProductType('passepartout')
      clearCombinedListingProductType('spacer')
      setCombinedListingSize(null)
      setCombinedListingPassepartoutHoleSize(null)

      // Update page URL
      const combinedListingUrlParameters: CombinedListingParameterMap = {
        products: `${product.productID}`,
        size: null,
        'cut-out': null,
        variant: null,
      }
      const newProductPageUrl = getPageUrl({
        locale,
        pageType: 'product',
        slugs: product.slug.current,
        parameters: combinedListingUrlParameters,
      })
      router.replace(newProductPageUrl)
    },
    [
      clearCombinedListingProductType,
      locale,
      router,
      setCombinedListingPassepartoutHoleSize,
      setCombinedListingSize,
    ],
  )

  // Load combined listing configuration
  useEffect(() => {
    if (product) {
      setCombinedListingStateManagerType('url')
      setCombinedListingMainProductId(product.productID)
      setCombinedListingConfiguration(product.productCombinedListing)
      setCombinedListingSettings(product.productCombinedListingSettings)
    }

    return () => {
      setCombinedListingStateManagerType('memory')
      setCombinedListingConfiguration()
      setCombinedListingSettings()
    }
  }, [
    product,
    setCombinedListingConfiguration,
    setCombinedListingMainProductId,
    setCombinedListingSettings,
    setCombinedListingStateManagerType,
  ])

  const frameSettings = useFrameSettings(
    artworkUrl ?? defaultArtworkUrl,
    backgroundColor,
  )

  if (!product) {
    return null
  }

  return (
    <section className="container grid grid-cols-1 md:grid-cols-2 gap-12 xl:gap-20 py-12">
      <div className="">
        <div
          ref={productGalleryWrapperRef}
          className="sticky top-[calc(var(--headerHeight)+3rem)]"
        >
          <ProductGallery
            photosets={product.galleryPhotos ?? []}
            frameSettings={frameSettings}
            preloadFirstImage
            hasDrag
            hasClickToZoom
            hasThumbnails
          />
        </div>
      </div>

      <div className="flex flex-col w-full space-y-5 max-w-3xl">
        <div className="mb-5">
          <h1 className="is-h2 mb-1">{product.title}</h1>

          <ProductPrice
            size="lg"
            price={price}
            comparePrice={comparePrice}
            showPrice
            showOldPrice
            showCurrency
            showPricePrefix={
              !combinedListingActiveVariants ||
              combinedListingActiveVariants.length === 0
            }
          />

          {/* {!!sku && (
            <div className="text-sm mt-6">
              {strings.productSkuLabel} {sku}
            </div>
          )} */}
        </div>

        <div className="space-y-3">
          <CombinedListingProductSelect
            openStep={openStep}
            frameColor={frameColor}
            isScreenSmallOrLarger={isScreenSmallOrLarger}
            onToggleStep={(step, isOpen) => {
              if (isOpen) {
                setOpenStep(step)
              }

              if (!isOpen) {
                setOpenStep(undefined)
              }
            }}
            onMainProductChange={handleMainProductChange}
          />

          {!!frameSettings && (
            <CombinedListingFramePreviewSettings
              open={openStep === 'preview'}
              predefinedBackgroundColors={predefinedBackgroundColors}
              defaultBackgroundColor={backgroundColor}
              isScreenSmallOrLarger={isScreenSmallOrLarger}
              setOpenStep={setOpenStep}
              onFileChange={setArtworkUrl}
              onBackgroundColorChange={setBackgroundColor}
            />
          )}

          <CombinedListingProductActions
            product={product}
            products={combinedListingActiveProducts ?? []}
            variants={combinedListingActiveVariants ?? []}
            price={price}
            size={combinedListingActiveSize ?? ''}
            passepartoutHoleSize={
              combinedListingActivePassepartoutHoleSize ?? undefined
            }
            isEmptyFrame={isEmptyFrame}
            cartAttributes={combinedListingCartAttributes}
          />

          {!!product.description && (
            <div className="pt-4 sm:pt-8">
              <SimplePortableText
                content={product.description}
                className="rc max-w-2xl"
              />
            </div>
          )}
        </div>
      </div>
    </section>
  )
}

export default CombinedListingProductHero
